import { Head } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import AgentTreeView, { TreeAgentNode } from '@/components/AgentTreeView'
import { GitBranch, Users, UserCheck, Clock, FileSignature } from 'lucide-react'

interface Props extends PageProps {
  nodes: TreeAgentNode[]
  agent_name: string
}

export default function DownlineTree({ nodes, agent_name }: Props) {
  // Summary stats computed client-side from the flat node list.
  // The first node (parent_agent_id === null) is always the root / current agent.
  const descendants    = nodes.filter((n) => n.parent_agent_id !== null)
  const totalDesc      = descendants.length
  const activeCount    = descendants.filter((n) => n.onboarding_status === 'active').length
  const pendingCount   = descendants.filter((n) => n.onboarding_status !== 'active').length
  const signedCount    = descendants.filter((n) => n.contract_signed).length

  const isEmpty = totalDesc === 0

  return (
    <AppLayout>
      <Head title="My Downline Tree" />

      <div className="space-y-4">
        {/* Page header */}
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">My Downline Tree</h1>
          <p className="text-chestnut-charcoal/70 mt-1">
            Full sponsorship network under {agent_name}
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="card px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-chestnut-forest/10 rounded-lg">
                <Users className="h-4 w-4 text-chestnut-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totalDesc}</p>
                <p className="text-xs text-chestnut-charcoal/60">Total Agents</p>
              </div>
            </div>
          </div>

          <div className="card px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCheck className="h-4 w-4 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{activeCount}</p>
                <p className="text-xs text-chestnut-charcoal/60">Active</p>
              </div>
            </div>
          </div>

          <div className="card px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-4 w-4 text-yellow-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{pendingCount}</p>
                <p className="text-xs text-chestnut-charcoal/60">Pending Onboarding</p>
              </div>
            </div>
          </div>

          <div className="card px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-chestnut-gold/15 rounded-lg">
                <FileSignature className="h-4 w-4 text-chestnut-gold" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{signedCount}</p>
                <p className="text-xs text-chestnut-charcoal/60">Agreements Signed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tree canvas or empty state */}
        {isEmpty ? (
          <div className="card flex flex-col items-center justify-center py-20 text-center">
            <div className="p-4 bg-chestnut-forest/10 rounded-full mb-4">
              <GitBranch className="h-8 w-8 text-chestnut-forest/60" />
            </div>
            <h3 className="text-lg font-semibold text-chestnut-charcoal mb-1">No downline yet</h3>
            <p className="text-chestnut-charcoal/60 max-w-xs text-sm">
              Agents you sponsor will appear here as a multi-level tree once they join.
            </p>
          </div>
        ) : (
          <div className="card overflow-hidden" style={{ height: 'calc(100vh - 320px)', minHeight: '500px' }}>
            <AgentTreeView
              agents={nodes}
              showFinancials={false}
              fetchChildrenUrl="/my/downline_tree/children"
            />
          </div>
        )}
      </div>
    </AppLayout>
  )
}
